/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 *
 * NOTE: this module can originally be found in the angular/universal repo
 * (https://github.com/angular/universal/tree/master/modules/common).
 *
 * This is a modified version which also supports POST calls
 */
import { HttpEvent, HttpHandler, HttpHeaders, HttpInterceptor, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { ApplicationRef, Injectable } from '@angular/core';
import { makeStateKey, StateKey, TransferState } from '@angular/platform-browser';
import { Observable, of as observableOf } from 'rxjs';
import { filter, take, tap } from 'rxjs/operators';

export interface TransferHttpResponse {
    body?: any | null;
    headers?: { [k: string]: string[] };
    status?: number;
    statusText?: string;
    url?: string;
}

function getHeadersMap(headers: HttpHeaders) {
    const headersMap: { [name: string]: string[] } = {};
    for (const key of headers.keys()) {
        headersMap[key] = headers.getAll(key)!;
    }
    return headersMap;
}

@Injectable()
export class TransferHttpCacheInterceptor implements HttpInterceptor {

    private isCacheActive = true;

    private invalidateCacheEntry(url: string) {
        Object.keys(this.transferState['store']).forEach(key =>
            key.includes(url) ? this.transferState.remove(makeStateKey(key)) : null
        );
    }

    private makeCacheKey(method: string, url: string, body: string | null | {}): StateKey<string>;
    private makeCacheKey(method: string, url: string, params: HttpParams | (string | null | {})): StateKey<string> {
        let encodedParams: string;
        if (params instanceof HttpParams) {
            // make the params encoded same as a url so it's easy to identify
            encodedParams = params.keys().sort().map(k => `${k}=${params.get(k)}`).join('&');
        }
        else {
            // Stringify the given body
            encodedParams = JSON.stringify(params);
        }

        const key = method.substr(0, 1) + '.' + url + '?' + encodedParams;

        return makeStateKey<TransferHttpResponse>(key);
    }

    constructor(appRef: ApplicationRef, private transferState: TransferState) {
        // Stop using the cache if the application has stabilized, indicating initial rendering is
        // complete.
        appRef.isStable
            .pipe(
                filter((isStable: boolean) => isStable),
                take(1)
            ).toPromise()
            .then(() => { this.isCacheActive = false; });
    }

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        // @TODO: How should we handle POST calls? Right now we're enablign all POST calls to be cached
        // Allow POST calls as long as there is no 'X-Parse-Session-Token' header
        if (req.method === 'POST' && req.headers.get('X-Parse-Session-Token') != null) {
            // Handle without caching, clear all matching urls from cache
            this.invalidateCacheEntry(req.url);
            return next.handle(req);
        }

        if (!this.isCacheActive) {
            // Cache is no longer active. Pass the request through.
            return next.handle(req);
        }

        const storeKey = this.makeCacheKey(req.method, req.url, (req.method === 'POST') ? req.body : req.params);

        if (this.transferState.hasKey(storeKey)) {
            // Request found in cache. Respond using it.
            const response = this.transferState.get(storeKey, {} as TransferHttpResponse);
            return observableOf(new HttpResponse<any>({
                body: response.body,
                headers: new HttpHeaders(response.headers),
                status: response.status,
                statusText: response.statusText,
                url: response.url,
            }));
        } else {
            // Request not found in cache. Make the request and cache it.
            const httpEvent = next.handle(req);
            return httpEvent
                .pipe(
                    tap((event: HttpEvent<any>) => {
                        if (event instanceof HttpResponse) {
                            this.transferState.set(storeKey, {
                                body: event.body,
                                headers: getHeadersMap(event.headers),
                                status: event.status,
                                statusText: event.statusText,
                                url: event.url!,
                            });
                        }
                    })
                );
        }
    }
}